import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Building2,
  User,
  Mail,
  Phone,
  Code2,
  Globe,
  Link2,
  RefreshCcw,
  Search,
  GraduationCap,
  Wrench,
  Server,
  Users,
  Calendar,
  Zap,
  FileText,
  Layers,
  ClipboardList,
  Monitor,
  ShoppingCart,
  LayoutGrid,
  Check,
  Database,
  Smartphone,
  BarChart2,
  Cpu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const PROJECT_TYPES = [
  // Développement & Intégration
  { value: "new_module",      label: "Développement sur mesure",  icon: Code2,         desc: "Application métier, module ou fonctionnalité spécifique", group: "dev" },
  { value: "extend_module",   label: "Extension / personnalisation", icon: Layers,     desc: "Adapter un logiciel existant (Odoo, ERP, CMS…)", group: "dev" },
  { value: "api_integration", label: "Intégration API / connecteur", icon: Link2,      desc: "Connecter deux outils ou synchroniser des données", group: "dev" },
  { value: "mobile_app",      label: "Application mobile",        icon: Smartphone,    desc: "App iOS / Android pour vos équipes ou clients", group: "dev" },
  // Web & Digital
  { value: "website",         label: "Création de site web",      icon: Monitor,       desc: "Vitrine, e-commerce, portail client, landing page", group: "web" },
  { value: "ecommerce",       label: "Boutique e-commerce",       icon: ShoppingCart,  desc: "Vente en ligne, catalogue produits, paiement sécurisé", group: "web" },
  // ERP / Odoo
  { value: "odoo_new",        label: "Mise en place ERP / Odoo",  icon: Database,      desc: "Déploiement, configuration et paramétrage Odoo", group: "erp" },
  { value: "migration",       label: "Migration / mise à jour",   icon: RefreshCcw,    desc: "Migrer vers Odoo ou passer à une version plus récente", group: "erp" },
  // Data & IA
  { value: "data_bi",         label: "Data & Business Intelligence", icon: BarChart2,  desc: "Tableaux de bord, reporting, analyse de données", group: "data" },
  { value: "automation",      label: "Automatisation & IA",       icon: Cpu,           desc: "Automatiser des processus, intégrer de l'IA métier", group: "data" },
  // Conseil
  { value: "audit",           label: "Audit & Conseil",           icon: Search,        desc: "Analyse, optimisation de votre système d'information", group: "conseil" },
  { value: "training",        label: "Formation",                 icon: GraduationCap, desc: "Former vos équipes sur vos outils et process", group: "conseil" },
];

const PROJECT_GROUPS = [
  { key: "dev",     label: "Développement" },
  { key: "web",     label: "Web & Digital" },
  { key: "erp",     label: "ERP / Odoo" },
  { key: "data",    label: "Data & IA" },
  { key: "conseil", label: "Conseil" },
];

const CURRENT_TOOLS = [
  { value: "odoo",       label: "Odoo" },
  { value: "sage",       label: "Sage" },
  { value: "dolibarr",   label: "Dolibarr" },
  { value: "ebp",        label: "EBP" },
  { value: "salesforce", label: "Salesforce" },
  { value: "excel",      label: "Excel / Google Sheets" },
  { value: "none",       label: "Aucun outil pour l'instant" },
  { value: "other",      label: "Autre" },
];

const COMPANY_SIZE = [
  { value: "solo",    label: "Indépendant / Freelance" },
  { value: "1_10",    label: "1 – 10 employés" },
  { value: "11_50",   label: "11 – 50 employés" },
  { value: "51_200",  label: "51 – 200 employés" },
  { value: "200plus", label: "200+ employés" },
];

const FUNCTIONAL_DOMAINS = [
  { id: "crm",        label: "Ventes & CRM" },
  { id: "account",    label: "Comptabilité & Facturation" },
  { id: "purchase",   label: "Achats & Fournisseurs" },
  { id: "stock",      label: "Stocks & Logistique" },
  { id: "project",    label: "Gestion de projets" },
  { id: "hr",         label: "Ressources Humaines" },
  { id: "website",    label: "Site web & e-Commerce" },
  { id: "mrp",        label: "Production / Fabrication" },
  { id: "rental",     label: "Location & Maintenance" },
  { id: "marketing",  label: "Marketing & Communication" },
  { id: "api",        label: "Intégration / API tierce" },
  { id: "unknown",    label: "Je ne sais pas encore" },
];

const TIMELINE_OPTIONS = [
  { value: "urgent_asap",  label: "Urgent / ASAP (< 1 mois)" },
  { value: "1_2months",    label: "1 – 2 mois" },
  { value: "2_4months",    label: "2 – 4 mois" },
  { value: "4_6months",    label: "4 – 6 mois" },
  { value: "6months_plus", label: "6 mois +" },
  { value: "flexible",     label: "Flexible / À définir" },
];

const URGENCY_OPTIONS = [
  { value: "planned", label: "Planifié",  desc: "Dans plus de 3 mois",  icon: Calendar },
  { value: "normal",  label: "Normal",    desc: "Sous 1 à 3 mois",      icon: ClipboardList },
  { value: "urgent",  label: "Urgent",    desc: "Sous 1 mois",          icon: Zap },
];

const HOW_FOUND = [
  { value: "web",      label: "Site web / Google" },
  { value: "referral", label: "Recommandation" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "event",    label: "Événement / Salon" },
  { value: "other",    label: "Autre" },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    project_types: [],
    current_tools: "",
    company_size: "",
    project_description: "",
    domains_concerned: [],
    integrations_needed: "",
    timeline: "",
    urgency: "",
    deadline: "",
    preferred_meeting: "",
    technical_constraints: "",
    how_found_us: "",
    additional_notes: "",
  });

  const totalSteps = 6;

  const steps = [
    { number: 1, title: "Contact",      icon: User },
    { number: 2, title: "Projet",       icon: Code2 },
    { number: 3, title: "Contexte",     icon: Server },
    { number: 4, title: "Périmètre",    icon: Layers },
    { number: 5, title: "Planning",     icon: Calendar },
    { number: 6, title: "Finalisation", icon: FileText },
  ];

  const set = (name, value) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const toggleProjectType = (value) =>
    setFormData((prev) => ({
      ...prev,
      project_types: prev.project_types.includes(value)
        ? prev.project_types.filter((v) => v !== value)
        : [...prev.project_types, value],
    }));

  const toggleDomain = (id) =>
    setFormData((prev) => ({
      ...prev,
      domains_concerned: prev.domains_concerned.includes(id)
        ? prev.domains_concerned.filter((m) => m !== id)
        : [...prev.domains_concerned, id],
    }));

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.company_name || !formData.contact_name || !formData.email) {
          toast.error("Veuillez remplir tous les champs obligatoires");
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          toast.error("Email invalide");
          return false;
        }
        return true;
      case 2:
        if (formData.project_types.length === 0) {
          toast.error("Veuillez sélectionner au moins un type de projet");
          return false;
        }
        return true;
      case 3:
        if (!formData.project_description || formData.project_description.length < 30) {
          toast.error("Décrivez votre besoin (minimum 30 caractères)");
          return false;
        }
        return true;
      case 5:
        if (!formData.timeline || !formData.urgency) {
          toast.error("Veuillez indiquer le délai et l'urgence");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) setCurrentStep((p) => Math.min(p + 1, totalSteps));
  };
  const prevStep = () => setCurrentStep((p) => Math.max(p - 1, 1));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        project_type: formData.project_types.join(", "),
        modules_concerned: formData.domains_concerned,
      };
      const response = await fetch(`${API_URL}/api/onboarding`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success("Votre demande a été envoyée !");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'envoi");
      }
    } catch {
      toast.error("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen" data-testid="onboarding-page">
        <Navbar />
        <section className="pt-32 pb-20 md:pt-40">
          <div className="nova-container">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Demande envoyée !
              </h1>
              <p className="text-slate-600 mb-4">
                Merci <strong>{formData.contact_name}</strong>. Notre équipe analyse votre
                demande et vous recontacte sous <strong>24h ouvrées</strong> pour
                un premier échange.
              </p>
              <p className="text-slate-500 text-sm mb-8">
                Un récapitulatif vous a été envoyé à{" "}
                <span className="font-medium text-slate-700">{formData.email}</span>.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button className="nova-btn-secondary">Retour à l'accueil</Button>
                </Link>
                <Link to="/portfolio">
                  <Button className="nova-btn-primary">Voir nos réalisations</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="onboarding-page">
      <Navbar />

      <section className="pt-32 pb-20 md:pt-36">
        <div className="nova-container">
          <div className="max-w-3xl mx-auto">

            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Décrivez votre projet
              </h1>
              <p className="text-slate-600">
                5 minutes pour nous donner les informations essentielles.
                On s'occupe du reste.
              </p>
            </div>

            {/* Progress Steps */}
            <div className="mb-12">
              <div className="flex justify-between items-center">
                {steps.map((step, index) => (
                  <div key={step.number} className="flex flex-col items-center relative">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                        currentStep >= step.number
                          ? "bg-indigo-600 border-indigo-600 text-white"
                          : "bg-white border-slate-200 text-slate-400"
                      }`}
                    >
                      <step.icon className="w-5 h-5" />
                    </div>
                    <span
                      className={`text-xs mt-2 font-medium hidden sm:block ${
                        currentStep >= step.number ? "text-indigo-600" : "text-slate-400"
                      }`}
                    >
                      {step.title}
                    </span>
                    {index < steps.length - 1 && (
                      <div
                        className={`absolute top-6 left-12 h-0.5 hidden sm:block ${
                          currentStep > step.number ? "bg-indigo-600" : "bg-slate-200"
                        }`}
                        style={{ width: "calc(100% + 24px)" }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Form Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12">

              {/* ── Step 1 : Contact ─────────────────────────────────────────── */}
              {currentStep === 1 && (
                <div className="space-y-6" data-testid="step-1">
                  <StepHeader icon={User} title="Vos coordonnées" />
                  <div className="grid sm:grid-cols-2 gap-6">
                    <FieldGroup label={<><Building2 className="w-4 h-4 inline mr-1"/>Entreprise *</>} htmlFor="company_name">
                      <Input id="company_name" name="company_name" value={formData.company_name}
                        onChange={(e) => set("company_name", e.target.value)}
                        placeholder="Nom de votre entreprise" className="nova-input" data-testid="input-company"/>
                    </FieldGroup>
                    <FieldGroup label={<><User className="w-4 h-4 inline mr-1"/>Nom complet *</>} htmlFor="contact_name">
                      <Input id="contact_name" name="contact_name" value={formData.contact_name}
                        onChange={(e) => set("contact_name", e.target.value)}
                        placeholder="Prénom Nom" className="nova-input" data-testid="input-contact"/>
                    </FieldGroup>
                    <FieldGroup label={<><Mail className="w-4 h-4 inline mr-1"/>Email professionnel *</>} htmlFor="email">
                      <Input id="email" name="email" type="email" value={formData.email}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder="vous@entreprise.com" className="nova-input" data-testid="input-email"/>
                    </FieldGroup>
                    <FieldGroup label={<><Phone className="w-4 h-4 inline mr-1"/>Téléphone</>} htmlFor="phone">
                      <Input id="phone" name="phone" value={formData.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        placeholder="+262 692 …" className="nova-input" data-testid="input-phone"/>
                    </FieldGroup>
                  </div>
                </div>
              )}

              {/* ── Step 2 : Type de projet (multi-sélection) ────────────────── */}
              {currentStep === 2 && (
                <div className="space-y-8" data-testid="step-2">
                  <StepHeader icon={Code2} title="Quel type de projet ?" />
                  <p className="text-slate-500 text-sm -mt-4">
                    Sélectionnez tout ce qui correspond à votre besoin — plusieurs choix possibles.
                  </p>

                  {PROJECT_GROUPS.map((group) => {
                    const groupTypes = PROJECT_TYPES.filter((t) => t.group === group.key);
                    return (
                      <div key={group.key}>
                        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">{group.label}</p>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {groupTypes.map((option) => {
                            const Icon = option.icon;
                            const selected = formData.project_types.includes(option.value);
                            return (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => toggleProjectType(option.value)}
                                className={`p-4 rounded-xl border-2 text-left transition-all flex items-start gap-3 ${
                                  selected ? "border-indigo-600 bg-indigo-50" : "border-slate-200 hover:border-indigo-200"
                                }`}
                                data-testid={`project-type-${option.value}`}
                              >
                                <div className="relative shrink-0 mt-0.5">
                                  <Icon className={`w-5 h-5 ${selected ? "text-indigo-600" : "text-slate-400"}`} />
                                  {selected && (
                                    <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-indigo-600 rounded-full flex items-center justify-center">
                                      <Check className="w-2.5 h-2.5 text-white" />
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <p className={`font-semibold text-sm ${selected ? "text-indigo-900" : "text-slate-800"}`}>{option.label}</p>
                                  <p className="text-xs text-slate-500 mt-0.5">{option.desc}</p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {formData.project_types.length > 0 && (
                    <p className="text-sm text-indigo-600 font-medium">
                      {formData.project_types.length} type{formData.project_types.length > 1 ? "s" : ""} sélectionné{formData.project_types.length > 1 ? "s" : ""}
                    </p>
                  )}
                </div>
              )}

              {/* ── Step 3 : Contexte ────────────────────────────────────────── */}
              {currentStep === 3 && (
                <div className="space-y-6" data-testid="step-3">
                  <StepHeader icon={Server} title="Votre contexte actuel" />

                  <FieldGroup label="Taille de votre structure" htmlFor="company_size">
                    <Select value={formData.company_size} onValueChange={(v) => set("company_size", v)}>
                      <SelectTrigger className="nova-input">
                        <SelectValue placeholder="Sélectionnez…" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPANY_SIZE.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>

                  <FieldGroup label="Outils / logiciels utilisés actuellement" htmlFor="current_tools">
                    <Select value={formData.current_tools} onValueChange={(v) => set("current_tools", v)}>
                      <SelectTrigger className="nova-input" data-testid="select-tools">
                        <SelectValue placeholder="Sélectionnez votre outil principal…" />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENT_TOOLS.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>

                  <FieldGroup label="Décrivez votre besoin en quelques lignes *" htmlFor="project_description">
                    <Textarea
                      id="project_description"
                      value={formData.project_description}
                      onChange={(e) => set("project_description", e.target.value)}
                      placeholder="Expliquez ce que vous souhaitez faire, le problème à résoudre ou le processus à améliorer…"
                      rows={5}
                      className="nova-input resize-none"
                      data-testid="input-description"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      {formData.project_description.length} / 30 caractères minimum
                    </p>
                  </FieldGroup>
                </div>
              )}

              {/* ── Step 4 : Périmètre fonctionnel ──────────────────────────── */}
              {currentStep === 4 && (
                <div className="space-y-6" data-testid="step-4">
                  <StepHeader icon={Layers} title="Périmètre fonctionnel" />
                  <p className="text-slate-500 text-sm">
                    Quels domaines sont concernés par votre projet ?
                    Cochez tout ce qui s'applique — ou "Je ne sais pas encore" si vous êtes au début de votre réflexion.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-3">
                    {FUNCTIONAL_DOMAINS.map((dom) => (
                      <label
                        key={dom.id}
                        className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.domains_concerned.includes(dom.id)
                            ? dom.id === "unknown"
                              ? "border-slate-400 bg-slate-50"
                              : "border-indigo-600 bg-indigo-50"
                            : "border-slate-200 hover:border-indigo-200"
                        }`}
                        data-testid={`domain-${dom.id}`}
                      >
                        <Checkbox
                          checked={formData.domains_concerned.includes(dom.id)}
                          onCheckedChange={() => toggleDomain(dom.id)}
                        />
                        <span className={`text-sm font-medium ${dom.id === "unknown" ? "text-slate-500 italic" : "text-slate-700"}`}>
                          {dom.label}
                        </span>
                      </label>
                    ))}
                  </div>

                  <FieldGroup label="Intégrations avec des outils tiers" htmlFor="integrations_needed">
                    <Textarea
                      id="integrations_needed"
                      value={formData.integrations_needed}
                      onChange={(e) => set("integrations_needed", e.target.value)}
                      placeholder="Ex : connecteur WooCommerce, API La Poste, Stripe, logiciel de caisse, autre ERP…"
                      rows={3}
                      className="nova-input resize-none"
                    />
                  </FieldGroup>
                </div>
              )}

              {/* ── Step 5 : Planning ────────────────────────────────────────── */}
              {currentStep === 5 && (
                <div className="space-y-6" data-testid="step-5">
                  <StepHeader icon={Calendar} title="Délais et priorité" />

                  <FieldGroup label="Délai de réalisation souhaité *" htmlFor="timeline">
                    <Select value={formData.timeline} onValueChange={(v) => set("timeline", v)}>
                      <SelectTrigger className="nova-input" data-testid="select-timeline">
                        <SelectValue placeholder="Sélectionnez…" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMELINE_OPTIONS.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>

                  <div className="space-y-2">
                    <Label>Niveau d'urgence *</Label>
                    <div className="grid grid-cols-3 gap-4 mt-2">
                      {URGENCY_OPTIONS.map(({ value, label, desc, icon: Icon }) => {
                        const selected = formData.urgency === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => set("urgency", value)}
                            className={`p-4 rounded-xl border-2 text-center transition-all ${
                              selected
                                ? "border-indigo-600 bg-indigo-50"
                                : "border-slate-200 hover:border-indigo-200"
                            }`}
                          >
                            <Icon className={`w-6 h-6 mx-auto mb-2 ${selected ? "text-indigo-600" : "text-slate-400"}`} />
                            <p className={`font-semibold text-sm ${selected ? "text-indigo-700" : "text-slate-700"}`}>{label}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <FieldGroup label="Date butoir (si applicable)" htmlFor="deadline">
                      <Input
                        id="deadline"
                        type="date"
                        value={formData.deadline}
                        onChange={(e) => set("deadline", e.target.value)}
                        className="nova-input"
                      />
                    </FieldGroup>
                    <FieldGroup label="Disponibilité pour un premier RDV" htmlFor="preferred_meeting">
                      <Select value={formData.preferred_meeting} onValueChange={(v) => set("preferred_meeting", v)}>
                        <SelectTrigger className="nova-input">
                          <SelectValue placeholder="Sélectionnez…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="this_week">Cette semaine</SelectItem>
                          <SelectItem value="next_week">Semaine prochaine</SelectItem>
                          <SelectItem value="no_rush">Sans urgence</SelectItem>
                        </SelectContent>
                      </Select>
                    </FieldGroup>
                  </div>
                </div>
              )}

              {/* ── Step 6 : Récapitulatif & finalisation ───────────────────── */}
              {currentStep === 6 && (
                <div className="space-y-6" data-testid="step-6">
                  <StepHeader icon={FileText} title="Récapitulatif & finalisation" />

                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 space-y-3 text-sm">
                    <SummaryRow label="Société" value={formData.company_name} />
                    <SummaryRow label="Contact" value={`${formData.contact_name} — ${formData.email}`} />
                    <SummaryRow
                      label="Type de projet"
                      value={formData.project_types.map(v => PROJECT_TYPES.find(p => p.value === v)?.label).filter(Boolean).join(", ")}
                    />
                    <SummaryRow label="Taille structure" value={COMPANY_SIZE.find(s=>s.value===formData.company_size)?.label} />
                    <SummaryRow label="Outils actuels" value={CURRENT_TOOLS.find(t=>t.value===formData.current_tools)?.label} />
                    <SummaryRow label="Domaines concernés" value={formData.domains_concerned.length > 0 ? FUNCTIONAL_DOMAINS.filter(d=>formData.domains_concerned.includes(d.id)).map(d=>d.label).join(', ') : '–'} />
                    <SummaryRow label="Délai" value={TIMELINE_OPTIONS.find(t=>t.value===formData.timeline)?.label} />
                    <SummaryRow label="Urgence" value={URGENCY_OPTIONS.find(u=>u.value===formData.urgency)?.label} />
                  </div>

                  <FieldGroup label="Contraintes techniques ou contexte important" htmlFor="technical_constraints">
                    <Textarea
                      id="technical_constraints"
                      value={formData.technical_constraints}
                      onChange={(e) => set("technical_constraints", e.target.value)}
                      placeholder="Accès serveur, VPN, contraintes de sécurité, compatibilités requises…"
                      rows={3}
                      className="nova-input resize-none"
                    />
                  </FieldGroup>

                  <FieldGroup label="Comment avez-vous connu Nova Forge ?" htmlFor="how_found_us">
                    <Select value={formData.how_found_us} onValueChange={(v) => set("how_found_us", v)}>
                      <SelectTrigger className="nova-input">
                        <SelectValue placeholder="Sélectionnez…" />
                      </SelectTrigger>
                      <SelectContent>
                        {HOW_FOUND.map((h) => (
                          <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>

                  <FieldGroup label="Notes complémentaires" htmlFor="additional_notes">
                    <Textarea
                      id="additional_notes"
                      value={formData.additional_notes}
                      onChange={(e) => set("additional_notes", e.target.value)}
                      placeholder="Toute autre information utile…"
                      rows={3}
                      className="nova-input resize-none"
                    />
                  </FieldGroup>

                  <div className="bg-indigo-50 rounded-xl p-4 text-sm text-indigo-700 border border-indigo-100">
                    En soumettant ce formulaire, vous acceptez d'être recontacté par
                    Nova Forge dans le cadre de votre demande.
                    Aucune donnée n'est transmise à des tiers.
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-10 pt-6 border-t border-slate-100">
                {currentStep > 1 ? (
                  <Button type="button" variant="outline" onClick={prevStep}
                    className="nova-btn-secondary" data-testid="btn-prev">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Précédent
                  </Button>
                ) : <div />}

                {currentStep < totalSteps ? (
                  <Button type="button" onClick={nextStep}
                    className="nova-btn-primary" data-testid="btn-next">
                    Suivant
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button type="button" onClick={handleSubmit} disabled={loading}
                    className="nova-btn-primary" data-testid="btn-submit">
                    {loading ? (
                      <><span className="animate-spin mr-2">⏳</span>Envoi…</>
                    ) : (
                      <><CheckCircle2 className="w-4 h-4 mr-2" />Envoyer ma demande</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ── Composants utilitaires ─────────────────────────────────────────────────── */

function StepHeader({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
        <Icon className="w-5 h-5 text-indigo-600" />
      </div>
      <h2 className="text-xl font-bold text-slate-900">{title}</h2>
    </div>
  );
}

function FieldGroup({ label, htmlFor, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}

function SummaryRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="text-slate-500 shrink-0 w-36">{label} :</span>
      <span className="text-slate-800 font-medium">{value}</span>
    </div>
  );
}
